import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import { listGoals, sendGoalsVoice, generateGoalsVoiceAudio, fmtCop, horizonLabel, getReallocationSuggestion, applyReallocation, getMacroImpact, getDistributionSuggestion } from '../lib/goals';

const NAVY = '#0A2540';
const BLUE = '#3d8bfd'; // Ola A accent (no purple)
const GREEN = '#16A34A';
const GOLD = '#C9A961';

const JOURNEY_LS_KEY = 'fp_metas_journey_dismissed';
const JOURNEY_STEPS = [
  { n: 1, title: 'Analizamos tus finanzas', text: 'Vemos cuánto puedes ahorrar cómodo, sin apretarte.' },
  { n: 2, title: 'Diseñamos tu plan', text: 'Cuánto ahorrar al mes y el portafolio ideal para tu plazo.' },
  { n: 3, title: 'Invertimos contigo', text: 'Tu dinero trabaja: el mercado también suma a tu meta.' },
  { n: 4, title: 'Te acompañamos', text: 'El Oráculo te sigue el paso y te motiva hasta lograrlo.' },
];

// Banner "te acompañamos en cada paso" — explica a fondo el valor de las Metas.
// El Oraculo PROACTIVO (Fase 1): sin que el cliente pregunte, detecta si una meta
// va adelantada y otra atrasada, y ofrece reequilibrar con un toque (sin SMS).
function ReallocationCard({ onApplied }) {
  const [sug, setSug] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    getReallocationSuggestion()
      .then((r) => { if (alive && r.hayOportunidad) setSug(r.sugerencia); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (dismissed || (!sug && !done)) return null;

  const apply = async () => {
    if (!sug || busy) return;
    setBusy(true);
    try {
      const res = await applyReallocation({ fromGoalId: sug.fromGoalId, toGoalId: sug.toGoalId, amountCop: sug.montoCop });
      if (res?.success) {
        setDone(res.data?.mensaje || 'Listo, reequilibré tus metas.');
        setSug(null);
        onApplied && onApplied();
      }
    } catch {} finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl px-5 py-4 mb-6 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
        <div className="text-sm font-semibold">✓ {done}</div>
        <div className="text-xs text-white/70 mt-1">Tus metas quedaron más parejas. Lo puedes deshacer cuando quieras.</div>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl px-5 py-4 mb-6 overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
      <button onClick={() => setDismissed(true)} aria-label="Cerrar" className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white/10 text-white/60 hover:text-white text-sm leading-none">×</button>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: GOLD }}>El Oráculo nota algo</div>
      <div className="text-white font-semibold text-sm leading-snug pr-6">
        Tu meta "{sug.fromGoalName}" va {sug.fromDiasAdelantado} días adelantada y "{sug.toGoalName}" va atrasada.
      </div>
      <div className="text-white/75 text-xs mt-1">
        Puedo mover <strong className="text-white">{fmtCop(sug.montoCop)}</strong> de una a otra para emparejarlas — sin que saques un peso de tu cuenta.
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={apply} disabled={busy} className="text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-60" style={{ background: GOLD, color: NAVY }}>
          {busy ? 'Reequilibrando...' : 'Reequilibrar por mí'}
        </button>
        <button onClick={() => setDismissed(true)} className="text-xs text-white/70 hover:text-white">Ahora no</button>
      </div>
    </div>
  );
}

// Ontologia: saldo libre -> metas. El Oraculo nota plata quieta y sugiere repartirla.
function DistributionCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let alive = true;
    getDistributionSuggestion()
      .then((d) => { if (alive && d?.hayOportunidad && d.distribucion?.length) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (dismissed || !data) return null;

  const usd = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD`;
  return (
    <div className="relative rounded-2xl px-5 py-4 mb-6 overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
      <button onClick={() => setDismissed(true)} aria-label="Cerrar" className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white/10 text-white/60 hover:text-white text-sm leading-none">×</button>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: GOLD }}>El Oráculo nota algo</div>
      <div className="text-white font-semibold text-sm leading-snug pr-6">
        Tienes <span style={{ color: GOLD }}>{usd(data.cashUsd)}</span> quietos, listos para invertir.
      </div>
      <div className="text-white/75 text-xs mt-1 mb-2">Mi sugerencia para que avancen tus metas atrasadas:</div>
      <div className="space-y-1.5">
        {data.distribucion.map((d, i) => (
          <button
            key={i}
            onClick={() => navigate(`/dashboard/metas/${d.goalId}`)}
            className="w-full flex items-center justify-between text-xs rounded-lg px-3 py-2 transition bg-white/10 hover:bg-white/20"
          >
            <span className="truncate pr-2">{d.nombre}</span>
            <span className="font-semibold shrink-0" style={{ color: GOLD }}>{usd(d.montoUsd)} →</span>
          </button>
        ))}
      </div>
      <div className="text-[11px] text-white/55 mt-2">Toca una meta para aportarle. Tú confirmas el monto.</div>
    </div>
  );
}

// Ontologia macro -> meta: como la inflacion (Progresion) impacta cada meta.
function MacroCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    getMacroImpact()
      .then((d) => { if (alive && d?.metas?.length) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!data) return null;

  const { inflacionPct, resumen } = data;
  const todasGanan = resumen.total > 0 && resumen.leGanan === resumen.total;
  const tag = (m) => {
    if (m.leGanaAInflacion) {
      return {
        txt: `Le gana por ${m.diferenciaPct.toFixed(1)} pts`,
        style: { background: 'rgba(22,163,74,0.18)', color: '#4ade80' },
      };
    }
    if (m.horizon === 'corto') {
      return {
        txt: 'Plazo corto, seguro',
        style: { background: 'rgba(154,168,184,0.18)', color: 'var(--fx-muted)' },
      };
    }
    return {
      txt: 'Empata la inflación',
      style: { background: 'rgba(201,169,97,0.18)', color: GOLD },
    };
  };

  return (
    <div className="metas-ola__card px-5 py-4 mb-6">
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="text-sm font-bold" style={{ color: 'var(--fx-text)' }}>Tu plata vs la inflación</div>
        <span
          className="text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0"
          style={{ background: 'rgba(201,169,97,0.18)', color: GOLD }}
        >
          Inflación {inflacionPct}% · Progresión
        </span>
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--fx-muted)' }}>
        {todasGanan
          ? `La vida sube ${inflacionPct}% al año, pero tu dinero invertido le gana. No pierdes poder de compra: lo proteges.`
          : `La inflación encarece la vida ${inflacionPct}% al año. Tu dinero invertido le hace frente.`}
      </p>
      <div className="space-y-1.5">
        {data.metas.map((m, i) => {
          const t = tag(m);
          return (
            <div key={i} className="flex items-center justify-between text-xs gap-2">
              <span className="truncate pr-2" style={{ color: 'var(--fx-text)' }}>{m.nombre}</span>
              <span className="font-medium shrink-0 px-2 py-0.5 rounded-full" style={t.style}>{t.txt}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetasJourney() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(JOURNEY_LS_KEY) === '1'; } catch { return false; }
  });
  if (hidden) return null;
  const dismiss = () => { try { localStorage.setItem(JOURNEY_LS_KEY, '1'); } catch {} setHidden(true); };

  return (
    <div className="relative rounded-[20px] px-6 py-6 mb-6 overflow-hidden text-white"
      style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1A3A6C 100%)' }}
    >
      <button onClick={dismiss} aria-label="Cerrar" type="button"
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white flex items-center justify-center text-base">
        ×
      </button>

      <div className="text-[10px] uppercase tracking-wider mb-1 font-bold" style={{ color: GOLD }}>
        Invertir con propósito
      </div>
      <div className="font-bold text-lg sm:text-xl mb-1 leading-tight">
        No ahorras solo. Te acompañamos en cada paso.
      </div>
      <p className="text-white/80 text-sm leading-relaxed mb-5 max-w-2xl">
        Desde que sueñas tu meta hasta que la logras, estamos contigo: analizamos tu bolsillo,
        armamos tu plan, invertimos por ti y te seguimos el paso.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {JOURNEY_STEPS.map((s) => (
          <div key={s.n} className="flex sm:flex-col gap-3 sm:gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
              style={{ background: GOLD, color: '#0A2540' }}>
              {s.n}
            </div>
            <div>
              <div className="text-sm font-semibold leading-tight">{s.title}</div>
              <div className="text-xs text-white/70 mt-0.5 leading-snug">{s.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Barra de dos colores: lo que aporto el cliente (azul) + lo que sumo el mercado (verde).
function TwoToneBar({ contributed, market, target }) {
  const t = Math.max(Number(target || 0), 1);
  const cPct = Math.min((Number(contributed || 0) / t) * 100, 100);
  const mPct = Math.min((Math.max(Number(market || 0), 0) / t) * 100, 100 - cPct);
  return (
    <div className="flex h-2.5 rounded-full overflow-hidden" style={{ background: 'color-mix(in srgb, var(--fx-muted) 18%, transparent)' }}>
      <div style={{ width: `${cPct}%`, background: BLUE }} />
      <div style={{ width: `${mPct}%`, background: GREEN }} />
    </div>
  );
}

export default function Goals() {
  const navigate = useNavigate();
  const snap = useSnapshot(state);
  const userPhone = snap.user?.phonenumber
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number || ''}`
    : '';
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceMsg, setVoiceMsg] = useState(null);
  const [listenLoading, setListenLoading] = useState(false);
  const [listenUrl, setListenUrl] = useState(null);

  useEffect(() => {
    let alive = true;
    listGoals()
      .then((g) => alive && setGoals(g))
      .catch((e) => alive && setErr(e.response?.data?.message || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // Escuchar el resumen de voz AQUI mismo (sin WhatsApp): genera y reproduce.
  const handleListen = async () => {
    if (listenLoading) return;
    setListenLoading(true);
    setVoiceMsg(null);
    try {
      const res = await generateGoalsVoiceAudio();
      if (res?.audioUrl) setListenUrl(`${res.audioUrl}?t=${Date.now()}`);
      else setVoiceMsg({ ok: false, text: 'No se pudo generar el audio. Intenta de nuevo.' });
    } catch (e) {
      setVoiceMsg({ ok: false, text: e.response?.data?.message || 'No se pudo generar el audio.' });
    } finally {
      setListenLoading(false);
    }
  };

  const handleVoice = async () => {
    if (!userPhone) { setVoiceMsg({ ok: false, text: 'No tienes un teléfono registrado en tu perfil.' }); return; }
    setVoiceLoading(true);
    setVoiceMsg(null);
    try {
      await sendGoalsVoice(userPhone);
      setVoiceMsg({ ok: true, text: 'Listo, te enviamos tu resumen a WhatsApp. Revísalo.' });
    } catch (e) {
      setVoiceMsg({ ok: false, text: e.response?.data?.message || 'No pudimos enviarlo. Intenta de nuevo.' });
    } finally {
      setVoiceLoading(false);
      setTimeout(() => setVoiceMsg(null), 6000);
    }
  };

  const reloadGoals = () => { listGoals().then(setGoals).catch(() => {}); };

  if (loading) return <div className="metas-ola px-4 sm:px-6 lg:px-8 py-8 text-sm" style={{ color: 'var(--fx-muted)' }}>Cargando tus metas...</div>;

  return (
    <div className="metas-ola px-4 sm:px-6 lg:px-8 py-6 max-w-3xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--fx-text)' }}>Mis Metas</h1>
          <p className="text-sm" style={{ color: 'var(--fx-muted)' }}>Invierte con proposito. Tu dinero ahorra contigo.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            onClick={() => navigate('/dashboard/metas/diagnostico')}
            className="text-sm px-4 py-2 rounded-lg font-medium border"
            style={{ borderColor: 'var(--fx-border)', color: 'var(--fx-text)', background: 'transparent' }}
          >
            ¿Cuánto puedo ahorrar?
          </button>
          <button
            onClick={() => navigate('/dashboard/metas/nueva')}
            className="text-sm px-4 py-2 rounded-lg text-white font-medium"
            style={{ background: 'var(--fx-accent)' }}
          >
            Crear meta
          </button>
        </div>
      </div>

      <MetasJourney />

      {goals.length >= 2 && <ReallocationCard onApplied={reloadGoals} />}

      {goals.length > 0 && <DistributionCard />}

      {goals.length > 0 && <MacroCard />}

      {goals.length > 0 && (
        <div className="mb-6">
          <div className="text-xs font-medium mb-2" style={{ color: 'var(--fx-muted)' }}>El Oráculo te cuenta cómo van tus metas, con voz</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleListen}
              disabled={listenLoading}
              className="flex items-center justify-center gap-2 flex-1 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-60"
              style={{ background: NAVY }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              {listenLoading ? 'Generando con voz...' : 'Escuchar aquí'}
            </button>
            <button
              onClick={handleVoice}
              disabled={voiceLoading}
              className="flex items-center justify-center gap-2 flex-1 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-60"
              style={{ background: '#25D366' }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.207zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
              {voiceLoading ? 'Enviando...' : 'Enviar a WhatsApp'}
            </button>
          </div>
          {listenUrl && (
            <audio controls autoPlay preload="none" src={listenUrl} className="w-full mt-3" style={{ height: 40 }} />
          )}
          {voiceMsg && (
            <div className={`text-xs mt-2 text-center ${voiceMsg.ok ? 'text-[#166534]' : 'text-red-600'}`}>{voiceMsg.text}</div>
          )}
        </div>
      )}

      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

      {goals.length === 0 ? (
        <div className="metas-ola__card px-6 py-10 text-center">
          <div className="text-base font-semibold mb-1" style={{ color: 'var(--fx-text)' }}>Todavia no tienes metas</div>
          <p className="text-sm mb-4" style={{ color: 'var(--fx-muted)' }}>
            Crea tu primera meta (una casa, un viaje, estudios) y el Oraculo te ayuda a llegar.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={() => navigate('/dashboard/metas/diagnostico')}
              className="text-sm px-4 py-2 rounded-lg font-medium border"
              style={{ borderColor: 'var(--fx-border)', color: 'var(--fx-text)', background: 'transparent' }}
            >
              ¿Cuánto puedo ahorrar?
            </button>
            <button
              onClick={() => navigate('/dashboard/metas/nueva')}
              className="text-sm px-4 py-2 rounded-lg text-white font-medium"
              style={{ background: 'var(--fx-accent)' }}
            >
              Crear mi primera meta
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map((g) => {
            const p = g.projection || {};
            return (
              <button
                key={g._id}
                onClick={() => navigate(`/dashboard/metas/${g._id}`)}
                className="metas-ola__card w-full text-left px-5 py-4 transition hover:brightness-[1.03]"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {g.icon && (
                      <span className="metas-ola__icon text-lg leading-none" aria-hidden>{g.icon}</span>
                    )}
                    <span className="text-sm font-semibold" style={{ color: 'var(--fx-text)' }}>{g.name}</span>
                  </div>
                  <span className="text-sm font-semibold" style={{ color: 'var(--fx-text)' }}>{Math.round(p.progressPct || 0)}%</span>
                </div>
                <TwoToneBar contributed={p.contributedCop} market={p.marketGainsCop} target={p.targetCop} />
                <div className="flex items-center justify-between mt-2 text-xs" style={{ color: 'var(--fx-muted)' }}>
                  <span>{fmtCop(p.currentCop)} de {fmtCop(p.targetCop)}</span>
                  <span>{horizonLabel(p.horizon)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
